"use client";

import type { ScaleTime } from "d3-scale";
import { useMotionValueEvent } from "motion/react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { clipRevealTransition } from "./animation";
import {
  defaultScatterColors,
  type TooltipData,
  useChartHover,
  useChartStable,
  useYScale,
} from "./chart-context";
import { useChartLegendHover } from "./chart-legend-hover";
import {
  type ChartProjection,
  ProjectedAxisLabels,
  projectView,
  useChartProjection,
  useProjectionProgress,
} from "./projection";
import {
  type ScatterProjectionValue,
  scatterFloorSlot,
  scatterLane,
  useScatterProjection,
} from "./scatter-projection-context";
import {
  getSeriesMarkerVisualExtent,
  ProjectedSeriesPointMarker,
  SeriesPointMarker,
  type SeriesPointMarkerStyle,
  StaticSeriesPointMarker,
} from "./series-point-marker";
import { bandCenterOffset } from "./x-scale";

export interface SeriesMarkersProps extends SeriesPointMarkerStyle {
  dataKey: string;
  /** Marker fill color. Defaults to series stroke or chart palette color. */
  fill?: string;
  /** Whether to animate markers with clip reveal. Default: true */
  animate?: boolean;
  /** Series name drawn at the end of its lane in the isometric projection. */
  label?: string;
  pointId?: (datum: Record<string, unknown>, index: number) => string | number;
  selectedIndex?: number | null;
}

const PILLAR_MIN_SHARE = 0.08;

interface PointAt {
  index: number;
  cx: number;
  cy: number;
  revealDelay: number;
}

interface MarkerStyle {
  fill: string;
  stroke: string;
  strokeWidth: number;
  ringGap: number;
  outlineWidth: number;
  outlineColor?: string;
  radius: number;
}

export function SeriesMarkers({
  dataKey,
  fill,
  stroke,
  strokeWidth = 2,
  ringGap = 2,
  outlineWidth = 0,
  outlineColor,
  radius = 5,
  animate = true,
  fadeOnHover = true,
  inactiveOpacity = 0.5,
  inactiveBlur = 2,
  enterBlur = 2,
  showActiveHighlight = true,
  label,
  pointId,
  selectedIndex = null,
}: SeriesMarkersProps) {
  // Stable slice only. Hover-driven dim + active-highlight live in the inner
  // <SeriesMarkersDimWrapper> / <SeriesMarkersActiveHighlight> components, so
  // mouse motion does not re-render the full point grid.
  const {
    data,
    xScale: ctxXScale,
    innerWidth,
    enterTransition,
    animationDuration,
    revealEpoch,
    isLoaded,
    xAccessor: ctxXAccessor,
    lines,
  } = useChartStable();
  const xScale = ctxXScale as ScaleTime<number, number>;
  const xAccessor = ctxXAccessor as (d: Record<string, unknown>) => Date;
  const projection = useChartProjection();
  const scatterProjection = useScatterProjection();
  const projectionProgress = useProjectionProgress(projection);
  const [progressActive, setProgressActive] = useState(
    () => projectionProgress.get() > 0
  );
  useMotionValueEvent(projectionProgress, "change", (value) =>
    setProgressActive(value > 0)
  );

  const seriesIndex = useMemo(() => {
    const index = lines.findIndex((line) => line.dataKey === dataKey);
    return index >= 0 ? index : 0;
  }, [lines, dataKey]);

  const seriesConfig = lines[seriesIndex];
  const yScale = useYScale(seriesConfig?.yAxisId);
  const seriesColor =
    defaultScatterColors[seriesIndex % defaultScatterColors.length] ??
    defaultScatterColors[0];

  const resolvedFill = fill ?? seriesConfig?.stroke ?? seriesColor;
  const resolvedStroke = stroke ?? resolvedFill;

  const visualExtent = useMemo(
    () =>
      getSeriesMarkerVisualExtent({
        radius,
        strokeWidth,
        ringGap,
        outlineWidth,
        showActiveHighlight,
      }),
    [radius, strokeWidth, ringGap, outlineWidth, showActiveHighlight]
  );

  const revealDurationSec =
    clipRevealTransition(enterTransition).duration ?? animationDuration / 1000;
  const enterDuration = 0.5;
  const isRevealing = animate && !isLoaded;

  const getY = useCallback(
    (d: Record<string, unknown>) => {
      const value = d[dataKey];
      return typeof value === "number" ? (yScale(value) ?? 0) : null;
    },
    [dataKey, yScale]
  );

  const points = useMemo<PointAt[]>(
    () =>
      data.flatMap((d, index) => {
        const cy = getY(d);
        if (cy === null) {
          return [];
        }
        const cx = (xScale(xAccessor(d)) ?? 0) + bandCenterOffset(xScale);
        const leadingEdge = Math.max(0, cx - visualExtent);
        const revealDelay =
          innerWidth > 0 && isRevealing
            ? (leadingEdge / innerWidth) * revealDurationSec
            : 0;

        return [{ index, cx, cy, revealDelay }];
      }),
    [
      data,
      getY,
      xScale,
      xAccessor,
      innerWidth,
      isRevealing,
      revealDurationSec,
      visualExtent,
    ]
  );

  // Memo so the inner <SeriesMarkersActiveHighlight> sees a stable prop and
  // can be cheaply re-rendered on hover without re-creating the spread.
  const markerStyle = useMemo<MarkerStyle>(
    () => ({
      fill: resolvedFill,
      stroke: resolvedStroke,
      strokeWidth,
      ringGap,
      outlineWidth,
      outlineColor,
      radius,
    }),
    [
      resolvedFill,
      resolvedStroke,
      strokeWidth,
      ringGap,
      outlineWidth,
      outlineColor,
      radius,
    ]
  );

  const activeScale = showActiveHighlight ? 1.35 : 1;

  const projected =
    projection !== null &&
    scatterProjection !== null &&
    (projection.isometric ||
      scatterProjection.pointInteraction ||
      !projection.settled ||
      progressActive ||
      projectionProgress.get() > 0);

  if (projected && projection && scatterProjection) {
    return (
      <ProjectedSeriesMarkers
        activeScale={activeScale}
        dataKey={dataKey}
        enterBlur={enterBlur}
        enterDuration={enterDuration}
        fadeOnHover={fadeOnHover}
        inactiveOpacity={inactiveOpacity}
        isRevealing={isRevealing}
        label={label}
        markerStyle={markerStyle}
        pointId={pointId}
        points={points}
        projection={projection}
        revealEpoch={revealEpoch ?? 0}
        scatterProjection={scatterProjection}
        selectedIndex={selectedIndex}
        seriesCount={lines.length}
        seriesIndex={seriesIndex}
      />
    );
  }

  if (isRevealing) {
    return (
      <g>
        {points.map((point) => (
          <SeriesPointMarker
            cx={point.cx}
            cy={point.cy}
            dataKey={dataKey}
            enterBlur={enterBlur}
            enterDuration={enterDuration}
            index={point.index}
            key={`${dataKey}-${point.index}`}
            revealDelay={point.revealDelay}
            revealEpoch={revealEpoch ?? 0}
            {...markerStyle}
          />
        ))}
      </g>
    );
  }

  // Stable base layer — its children come from the parent and stay
  // referentially identical when the dim wrapper re-renders for hover.
  const baseMarkers = points.map((point) => (
    <StaticSeriesPointMarker
      cx={point.cx}
      cy={point.cy}
      key={`${dataKey}-${point.index}`}
      {...markerStyle}
    />
  ));

  return (
    <g>
      <SeriesMarkersDimWrapper
        enabled={fadeOnHover}
        inactiveBlur={inactiveBlur}
        inactiveOpacity={inactiveOpacity}
        seriesIndex={seriesIndex}
      >
        {baseMarkers}
      </SeriesMarkersDimWrapper>
      <SeriesMarkersActiveHighlight
        activeScale={activeScale}
        enabled={fadeOnHover}
        markerStyle={markerStyle}
        points={points}
      />
    </g>
  );
}

SeriesMarkers.displayName = "SeriesMarkers";

interface ProjectedSeriesMarkersProps {
  projection: ChartProjection;
  scatterProjection: ScatterProjectionValue;
  dataKey: string;
  label?: string;
  pointId?: (datum: Record<string, unknown>, index: number) => string | number;
  selectedIndex: number | null;
  seriesIndex: number;
  seriesCount: number;
  points: PointAt[];
  markerStyle: MarkerStyle;
  activeScale: number;
  fadeOnHover: boolean;
  inactiveOpacity: number;
  isRevealing: boolean;
  revealEpoch: number;
  enterDuration: number;
  enterBlur: number;
}

function ProjectedSeriesMarkers({
  projection,
  scatterProjection,
  dataKey,
  label,
  pointId,
  selectedIndex,
  seriesIndex,
  seriesCount,
  points,
  markerStyle,
  activeScale,
  fadeOnHover,
  inactiveOpacity,
  isRevealing,
  revealEpoch,
  enterDuration,
  enterBlur,
}: ProjectedSeriesMarkersProps) {
  const { data, innerHeight, innerWidth } = useChartStable();
  const { tooltipData } = useChartHover();
  const { filterIndex: legendActiveIndex, toggleSelectedIndex } =
    useChartLegendHover();
  const {
    slots,
    laneDepth,
    layout,
    pillarExtent,
    pillarFootprint,
    interactive,
    enter,
    leave,
    tap,
  } = scatterProjection;
  const isFloor = layout === "floor";
  const lane = scatterLane(seriesIndex, seriesCount);
  const laneY = (lane + 0.5) * laneDepth;
  const slot = isFloor ? undefined : slots.get(lane);
  const isLegendDimmed =
    legendActiveIndex !== null && legendActiveIndex !== seriesIndex;

  const ordered = useMemo(
    () => [...points].sort((a, b) => a.cx - b.cx || a.index - b.index),
    [points]
  );

  const anchorView = projection.at(projection.isometric ? 1 : 0);
  const activeIndex =
    tooltipData !== null
      ? tooltipData.yPositions[dataKey] !== undefined
        ? tooltipData.index
        : null
      : selectedIndex;
  const anyActive = tooltipData !== null || selectedIndex !== null;

  const markers = ordered.map((point) => {
    const pointY = isFloor ? point.cy : laneY;
    const z = isFloor
      ? pillarExtent *
        (PILLAR_MIN_SHARE +
          (1 - PILLAR_MIN_SHARE) *
            Math.min(1, Math.max(0, (innerHeight - point.cy) / innerHeight)))
      : innerHeight - point.cy;
    const key = `${dataKey}:${point.index}`;
    const anchor = projectView(anchorView, point.cx, pointY, z);
    const tooltip = (): TooltipData => ({
      point: data[point.index] ?? {},
      index: point.index,
      x: anchor[0],
      yPositions: { [dataKey]: anchor[1] },
      xPositions: { [dataKey]: anchor[0] },
    });
    const hovered = activeIndex === point.index;
    const faded = isLegendDimmed || (fadeOnHover && anyActive && !hovered);
    const marker = (
      <ProjectedSeriesPointMarker
        dataKey={dataKey}
        enterBlur={enterBlur}
        enterDuration={enterDuration}
        highlighted={hovered}
        index={point.index}
        interactive={interactive}
        key={key}
        onClick={(event) =>
          toggleSelectedIndex(seriesIndex, {
            element: event.currentTarget,
            kind: "point",
            id: pointId
              ? pointId(data[point.index] ?? {}, point.index)
              : `${seriesIndex}:${point.index}`,
          })
        }
        onPointerEnter={
          interactive
            ? (event) => {
                if (event.pointerType !== "touch") {
                  enter(tooltip(), key);
                }
              }
            : undefined
        }
        onPointerLeave={
          interactive
            ? (event) => {
                if (event.pointerType !== "touch") {
                  leave();
                }
              }
            : undefined
        }
        onTouchStart={interactive ? () => tap(tooltip(), key) : undefined}
        opacity={faded ? inactiveOpacity : 1}
        pillar={isFloor ? pillarFootprint : undefined}
        projection={projection}
        revealDelay={point.revealDelay}
        revealEpoch={revealEpoch}
        revealing={isRevealing}
        scale={hovered && fadeOnHover ? activeScale : 1}
        x={point.cx}
        y={pointY}
        z={z}
        {...markerStyle}
      />
    );
    if (!isFloor) {
      return marker;
    }
    const floorSlot = slots.get(
      scatterFloorSlot(point.index, seriesIndex, seriesCount)
    );
    return floorSlot ? createPortal(marker, floorSlot, key) : null;
  });

  return (
    <>
      {label && seriesCount > 1 && !isFloor ? (
        <ProjectedAxisLabels
          axis="y"
          dx={6}
          dy={4}
          labels={[
            {
              key: dataKey,
              text: label,
              x: innerWidth,
              y: laneY,
              z: 0,
            },
          ]}
          textAnchor="start"
        />
      ) : null}
      {isFloor
        ? markers
        : slot
          ? createPortal(<g>{markers}</g>, slot, dataKey)
          : null}
    </>
  );
}

interface SeriesMarkersDimWrapperProps {
  enabled: boolean;
  inactiveOpacity: number;
  inactiveBlur: number;
  seriesIndex: number;
  children: ReactNode;
}

/**
 * Wraps the stable point grid with hover-driven opacity + blur. Subscribes to
 * hover internally so the grid (passed as `children`) keeps a stable reference
 * and React skips reconciling it when this wrapper re-renders.
 */
function SeriesMarkersDimWrapper({
  enabled,
  inactiveOpacity,
  inactiveBlur,
  seriesIndex,
  children,
}: SeriesMarkersDimWrapperProps) {
  const { tooltipData } = useChartHover();
  // filterIndex, not selectedIndex: only a legend selection may single out a
  // series. An element tap neither hides the others nor marks the legend row.
  const { filterIndex: legendActiveIndex, toggleSelectedIndex } =
    useChartLegendHover();
  const isLegendDimmed =
    legendActiveIndex !== null && legendActiveIndex !== seriesIndex;
  const dimBase = enabled && (tooltipData !== null || isLegendDimmed);
  return (
    <g
      opacity={dimBase ? inactiveOpacity : 1}
      onClick={
        seriesIndex !== undefined
          ? (e: { currentTarget: Element }) =>
              toggleSelectedIndex(seriesIndex, {
                element: e.currentTarget,
                kind: "series",
              })
          : undefined
      }
      style={{
        transition: "opacity 0.15s ease-in-out, filter 0.15s ease-in-out",
        filter:
          dimBase && inactiveBlur > 0 ? `blur(${inactiveBlur}px)` : "none",
        cursor: seriesIndex !== undefined ? "pointer" : undefined,
      }}
    >
      {children}
    </g>
  );
}

interface SeriesMarkersActiveHighlightProps {
  enabled: boolean;
  points: PointAt[];
  markerStyle: MarkerStyle;
  activeScale: number;
}

/**
 * Renders the scaled "active" marker on top of the base grid. Subscribes to
 * hover internally; the parent doesn't re-render on cursor motion.
 */
function SeriesMarkersActiveHighlight({
  enabled,
  points,
  markerStyle,
  activeScale,
}: SeriesMarkersActiveHighlightProps) {
  const { tooltipData } = useChartHover();
  if (!enabled || tooltipData === null) {
    return null;
  }
  const activePoint = points.find((point) => point.index === tooltipData.index);
  if (!activePoint) {
    return null;
  }
  return (
    <StaticSeriesPointMarker
      cx={activePoint.cx}
      cy={activePoint.cy}
      scale={activeScale}
      {...markerStyle}
    />
  );
}

export default SeriesMarkers;
